import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  Trophy, ArrowLeft, Loader, Medal, Star, Crown
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { useLanguage } from "../hooks/useLanguage";

export default function ChurchLeaderboardPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const { data } = await axios.get(`${API}/church/leaderboard`);
      setLeaderboard(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return { bg: "linear-gradient(135deg, #FFD166, #F77F00)", icon: Crown, iconColor: "#fff" };
      case 2:
        return { bg: "linear-gradient(135deg, #C0C0C0, #A0A0A0)", icon: Medal, iconColor: "#fff" };
      case 3:
        return { bg: "linear-gradient(135deg, #CD7F32, #B87333)", icon: Medal, iconColor: "#fff" };
      default:
        return { bg: "#F0F4FF", icon: null, iconColor: null };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#FFD166" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #FFD166 0%, #F77F00 100%)" }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/church")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4">
            <ArrowLeft size={16} /> {t('back')}
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-2xl p-3">
              <Trophy size={28} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('leaderboard')}
              </h1>
              <p className="text-white/70 text-sm font-medium">{t('top_bible_learners')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-5">
        {leaderboard.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <Trophy size={48} className="mx-auto mb-3 opacity-40" color="#FFD166" />
            <p className="text-gray-500 font-medium">{t('no_data_yet')}</p>
            <p className="text-gray-400 text-sm">{t('children_appear_here')}</p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 mb-5">
                <div className="flex items-end justify-center gap-4">
                  {/* 2nd Place */}
                  <div className="text-center">
                    <div className="relative">
                      <AvatarDisplay avatar={leaderboard[1]?.avatar} size={56} />
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                        <span className="text-xs font-black text-white">2</span>
                      </div>
                    </div>
                    <p className="font-bold text-sm text-[#073B4C] mt-2 truncate max-w-20">
                      {leaderboard[1]?.child_name}
                    </p>
                    <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                      <Star size={10} fill="#FFD166" color="#FFD166" /> {leaderboard[1]?.total_stars}
                    </p>
                  </div>

                  {/* 1st Place */}
                  <div className="text-center -mt-4">
                    <Crown size={24} color="#FFD166" className="mx-auto mb-1" />
                    <div className="relative">
                      <AvatarDisplay avatar={leaderboard[0]?.avatar} size={72} />
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-yellow-500 flex items-center justify-center">
                        <span className="text-sm font-black text-white">1</span>
                      </div>
                    </div>
                    <p className="font-black text-[#073B4C] mt-2 truncate max-w-24">
                      {leaderboard[0]?.child_name}
                    </p>
                    <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                      <Star size={10} fill="#FFD166" color="#FFD166" /> {leaderboard[0]?.total_stars}
                    </p>
                  </div>

                  {/* 3rd Place */}
                  <div className="text-center">
                    <div className="relative">
                      <AvatarDisplay avatar={leaderboard[2]?.avatar} size={56} />
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: "#CD7F32" }}>
                        <span className="text-xs font-black text-white">3</span>
                      </div>
                    </div>
                    <p className="font-bold text-sm text-[#073B4C] mt-2 truncate max-w-20">
                      {leaderboard[2]?.child_name}
                    </p>
                    <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                      <Star size={10} fill="#FFD166" color="#FFD166" /> {leaderboard[2]?.total_stars}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Full List */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <p className="font-bold text-gray-500 text-sm">All Participants</p>
              </div>
              <div className="divide-y">
                {leaderboard.map((entry) => {
                  const style = getRankStyle(entry.rank);
                  
                  return (
                    <div key={entry.child_id} className="flex items-center gap-3 p-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${
                        entry.rank <= 3 ? "text-white" : "bg-gray-100 text-gray-500"
                      }`}
                        style={entry.rank <= 3 ? { background: style.bg } : {}}>
                        {entry.rank}
                      </div>
                      
                      <AvatarDisplay avatar={entry.avatar} size={40} />
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#073B4C] truncate">{entry.child_name}</p>
                        <p className="text-xs text-gray-400 truncate">
                          {entry.family_name} · {entry.age_group === "little" ? "Little Light" : entry.age_group === "big" ? "Explorer" : "Faith Builder"}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="font-black text-[#073B4C] flex items-center gap-1">
                          <Star size={14} fill="#FFD166" color="#FFD166" /> {entry.total_stars}
                        </p>
                        <p className="text-xs text-gray-400">{entry.lessons_completed} lessons</p>
                      </div>

                      {entry.active_today && (
                        <div className="w-2 h-2 rounded-full bg-green-500" title="Active today" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
